// useNummernAndererKontakte.ts — „Diese Nummer gehört schon zu <Kontakt>"
// (User 01.10.2026): Telefon-/Mobilnummern, die bei einem ANDEREN Kontakt
// stehen.
//
// Fragt die vorhandene Schnellwahl-Suche `GET /telephony/dial-search` (RBAC-
// und mandantengebunden, dieselbe wie im Anruf-Widget) und prüft die Treffer
// hier noch einmal genau: Der Server vergleicht nur die letzten Ziffern, als
// „dieselbe Nummer" zählt aber nur derselbe `wegVergleichsSchluessel`.
//
// Sparsam:
// - nur vollständige Telefon-/Mobilnummern (Fax kennt die Suche nicht),
// - erst 600 ms nach dem letzten Tippen,
// - je Nummer höchstens EINE Anfrage pro Maske (Ergebnis gemerkt),
// - in der Akte (`nurGeaenderte`) nur Nummern, die sich gegenüber dem
//   Ladestand geändert haben — sonst feuerte jedes Öffnen einer Akte mehrere
//   Suchen, und die gemeinsame Festnetznummer der Eheleute stünde dauernd als
//   Hinweis da.
// Fehler (z. B. ohne Telefonie-Recht 403) bleiben still: dann kein Hinweis.

import * as React from "react";
import { telephonyService } from "../../../services/telephonyService";
import { wegVergleichsSchluessel } from "../../../utils/buildPrivateContactCreatePayload";
import { wegSchluessel, type KontaktWeg } from "./doppelteWege";

export interface AndererKontakt {
  contactId: number;
  displayName: string;
}

/** Ab so vielen Ziffern gilt eine Nummer als fertig getippt. */
const MIN_ZIFFERN = 9;

const pruefbar = (w: KontaktWeg): string | null =>
  (w.typ === "phone" || w.typ === "mobile") && w.wert.replace(/\D/g, "").length >= MIN_ZIFFERN
    ? wegSchluessel(w.typ, w.wert)
    : null;

export function useNummernAndererKontakte(
  wege: readonly KontaktWeg[],
  opts: {
    /** Der Kontakt selbst (Akte/Umwandeln) — zählt nicht als „anderer". */
    eigeneId?: number | null;
    /**
     * Nur Werte prüfen, die vom ersten befüllten Stand abweichen (Akte). In der
     * Anlage (`false`) zählt alles, was eingetragen wird.
     */
    nurGeaenderte?: boolean;
    aktiv?: boolean;
  } = {},
): Map<string, AndererKontakt> {
  const { eigeneId, nurGeaenderte = false, aktiv = true } = opts;
  // Schlüssel → Treffer (null = geprüft, keiner). Bleibt über Renders erhalten.
  const cache = React.useRef(new Map<string, AndererKontakt | null>());
  const laufend = React.useRef(new Set<string>());
  const [, neuZeichnen] = React.useReducer((x: number) => x + 1, 0);

  // Ladestand der Akte: die Werte beim ersten Render, in dem überhaupt ein
  // Kommunikationsfeld befüllt ist.
  const ladestand = React.useRef<Map<string, string> | null>(null);
  if (nurGeaenderte && !ladestand.current && wege.some((w) => w.wert.trim())) {
    ladestand.current = new Map(wege.map((w) => [w.key, w.wert.trim()]));
  }

  const zuPruefen = new Map<string, string>(); // feld-key → schlüssel
  if (aktiv) {
    for (const w of wege) {
      const k = pruefbar(w);
      if (!k) continue;
      if (nurGeaenderte && ladestand.current?.get(w.key) === w.wert.trim()) continue;
      zuPruefen.set(w.key, k);
    }
  }
  const offen = [...new Set(zuPruefen.values())].filter((k) => !cache.current.has(k) && !laufend.current.has(k));
  const offenSig = offen.join("|");
  const eigeneIdRef = React.useRef(eigeneId);
  eigeneIdRef.current = eigeneId;
  // Original-Schreibweise je Schlüssel (für die Suche).
  const roh = new Map<string, string>();
  for (const w of wege) {
    const k = zuPruefen.get(w.key);
    if (k && !roh.has(k)) roh.set(k, w.wert.trim());
  }
  const rohRef = React.useRef(roh);
  rohRef.current = roh;

  const aktivRef = React.useRef(true);
  React.useEffect(() => {
    aktivRef.current = true;
    return () => {
      aktivRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (!offenSig) return;
    const schluessel = offenSig.split("|");
    // Laufende Anfragen werden NICHT abgebrochen: Sobald eine läuft, fällt ihr
    // Schlüssel aus `offenSig` — ein Abbruch beim nächsten Render ließe sie nie
    // ankommen. Weitergetippt wird ohnehin unter einem neuen Schlüssel.
    const t = window.setTimeout(() => {
      for (const k of schluessel) {
        const nummer = rohRef.current.get(k);
        if (!nummer) continue;
        laufend.current.add(k);
        telephonyService
          .dialSearch(nummer, { limit: 10 })
          .then((treffer) => {
            const ziel = k.slice(2); // ohne „n:"
            const hit = (Array.isArray(treffer) ? treffer : []).find(
              (t) =>
                t.contactId !== eigeneIdRef.current &&
                (t.numbers ?? []).some((n) => wegVergleichsSchluessel(n.value) === ziel),
            );
            cache.current.set(k, hit ? { contactId: hit.contactId, displayName: hit.displayName } : null);
          })
          .catch(() => {
            cache.current.set(k, null);
          })
          .finally(() => {
            laufend.current.delete(k);
            if (aktivRef.current) neuZeichnen();
          });
      }
    }, 600);
    return () => window.clearTimeout(t);
  }, [offenSig]);

  const out = new Map<string, AndererKontakt>();
  for (const [feld, k] of zuPruefen) {
    const hit = cache.current.get(k);
    if (hit && hit.contactId !== eigeneId) out.set(feld, hit);
  }
  return out;
}
